// Pure evaluation of the low-resolution-images notification. Kept free of the
// redux store / api imports so it can be unit tested in isolation.
import { propertyNavRoutes } from '../components/Default/Property/NavigationSidebar/NavigationHelper'

export const MIN_IMAGE_WIDTH = 1920
export const MIN_IMAGE_HEIGHT = 1080

/**
 * An image is low resolution when its known dimensions are below 1920x1080.
 * Images without dimensions are not flagged.
 *
 * @param {{ width: ?number, height: ?number }} image
 * @returns {boolean}
 */
export const isLowResolutionImage = (image) => {
  return !!(image?.width && image?.height && (image.width < MIN_IMAGE_WIDTH || image.height < MIN_IMAGE_HEIGHT))
}

/**
 * Counts the low-resolution images and decides where the customer reviews them:
 * the edit-rooms view when only room-gallery images are affected, otherwise the
 * images view (property gallery or unassigned images).
 *
 * Gallery entries for rooms that no longer exist are ignored, since edit-rooms
 * can't show their images.
 *
 * @param {Array<Object>} images the property's images
 * @param {{ property: { order: Array<string> }, rooms: Object }} galleries the property's image galleries
 * @param {Array<{ id: string }>} rooms the property's rooms (may be null)
 * @returns {?{ count: number, target: string }} null when no image is low resolution
 */
export const evaluateLowResolutionImages = (images, galleries, rooms) => {
  const affectedIds = (images ?? []).filter(isLowResolutionImage).map(image => image.id)
  if (affectedIds.length === 0) {
    return null
  }

  const propertyImageIds = new Set(galleries?.property?.order ?? [])
  const roomImageIds = new Set(
    (rooms ?? []).flatMap(room => galleries?.rooms?.[room.id]?.order ?? [])
  )
  const onlyRoomImages = affectedIds.every(id => roomImageIds.has(id) && !propertyImageIds.has(id))

  return {
    count: affectedIds.length,
    target: onlyRoomImages ? propertyNavRoutes.editRooms : propertyNavRoutes.images,
  }
}
